import { useState } from "react";
import { useDocStore } from "../state/docStore";
import { submit as submitApi } from "../lib/apiClient";
import { Button } from "./common/Button";
import "../pages/Workspace.css";

interface SubmitModalProps {
  onClose: () => void;
}

const CONTACT_URL = "https://aisingapore.org/home/contact-us/";

export function SubmitModal({ onClose }: SubmitModalProps) {
  const { doc } = useDocStore();
  const [phase, setPhase] = useState<"confirm" | "submitting" | "done">(
    "confirm",
  );
  const [error, setError] = useState<string | null>(null);

  const stretch = doc.stretchGoals.filter((g) => g.trim());

  const handleConfirm = async () => {
    setError(null);
    setPhase("submitting");
    try {
      await submitApi(doc);
      setPhase("done");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Submission failed. Please try again.",
      );
      setPhase("confirm");
    }
  };

  return (
    <div
      className="modal__overlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && phase !== "submitting") onClose();
      }}
    >
      <div className="modal">
        {phase === "done" ? (
          <>
            <div className="modal__success-icon" aria-hidden="true">
              ✓
            </div>
            <h2 className="modal__title">Submitted</h2>
            <div className="modal__body">
              <p>
                Your project document has been saved and emailed to the LADP team
                and to you. You can still download the Word/PDF anytime.
              </p>
              <p style={{ marginTop: "var(--space-3)" }}>
                To check the status of your submission, contact the team at{" "}
                <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                  aisingapore.org/home/contact-us
                </a>{" "}
                and select topic <strong>LADP</strong>.
              </p>
            </div>
            <div className="modal__actions">
              <Button variant="primary" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="modal__title">Submit your scoped project?</h2>
            <div className="modal__body">
              <p>
                This sends your document to the LADP team and emails a copy to
                you. Please confirm the scope below.
              </p>
              <dl className="modal__summary">
                <dt>Name</dt>
                <dd>{doc.participantName.trim() || "—"}</dd>
                <dt>Contact email</dt>
                <dd>{doc.contactEmail.trim() || "—"}</dd>
                <dt>Company</dt>
                <dd>{doc.companyName.trim() || "—"}</dd>
                <dt>Main goal</dt>
                <dd>{doc.mainGoal.trim() || "—"}</dd>
                <dt>Stretch goals</dt>
                <dd>
                  {stretch.length === 0 ? (
                    "None"
                  ) : (
                    <ol style={{ margin: 0, paddingLeft: "var(--space-5)" }}>
                      {stretch.map((g, i) => (
                        <li key={i}>{g}</li>
                      ))}
                    </ol>
                  )}
                </dd>
              </dl>
            </div>
            {error && <div className="modal__error">{error}</div>}
            <div className="modal__actions">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={phase === "submitting"}
              >
                Keep editing
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                loading={phase === "submitting"}
              >
                Confirm &amp; submit
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
